import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationManager } from 'react-notifications';
import base from '@/_api/base';
import { useApiMutation } from '@/hooks/api/useApiMutation';
import { useGetQuery } from '@/hooks/api/useGetQuery';
import { queryKeys } from '../query-keys';

export function useSponsorsQuery(festivalId) {
  return useGetQuery(
    () => `/admin/sponsors/${festivalId}`,
    {},
    queryKeys.sponsors(festivalId),
    { enabled: festivalId != null && festivalId !== '' },
  );
}

export function useSponsorsForObjectQuery(festivalId, objectType, objectId) {
  return useGetQuery(
    () => `/admin/sponsors/${festivalId}/${objectType}/${objectId}`,
    {},
    queryKeys.sponsorsForObject(festivalId, objectType, objectId),
    {
      enabled:
        festivalId != null &&
        festivalId !== '' &&
        objectType &&
        objectId != null &&
        objectId !== '',
    },
  );
}

export function useSaveSponsorMutation(festivalId) {
  const queryClient = useQueryClient();
  return useApiMutation(
    (sponsor) => `/admin/sponsor/${festivalId}`,
    'post',
    {},
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sponsors(festivalId),
        });
        NotificationManager.success('Sponsor saved', '', 3000);
      },
      onError: () => {
        NotificationManager.error('Sponsor could not be saved', '', 3000);
      },
    },
  );
}

export function useRemoveSponsorMutation(festivalId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sponsorId) => base.delete(`/admin/sponsor/${sponsorId}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sponsors(festivalId),
      });
      NotificationManager.success('Sponsor removed', '', 3000);
    },
    onError: () => {
      NotificationManager.error('Sponsor could not be removed', '', 3000);
    },
  });
}

export function useAssociateSponsorMutation(festivalId, objectType, objectId) {
  const queryClient = useQueryClient();
  return useApiMutation(
    ({ sponsorId }) =>
      `/admin/sponsor/${festivalId}/${sponsorId}/${objectType}/${objectId}`,
    'put',
    {},
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sponsors(festivalId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.sponsorsForObject(
            festivalId,
            objectType,
            objectId,
          ),
        });
        NotificationManager.success('Sponsor added', '', 3000);
      },
      onError: () => {
        NotificationManager.error('Sponsor could not be added', '', 3000);
      },
    },
    (_vars) => ({}),
  );
}

export function useDisassociateSponsorMutation(
  festivalId,
  objectType,
  objectId,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sponsorId) =>
      base.delete(
        `/admin/sponsor/${festivalId}/${sponsorId}/${objectType}/${objectId}`,
        {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sponsors(festivalId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sponsorsForObject(festivalId, objectType, objectId),
      });
      NotificationManager.success('Sponsor removed', '', 3000);
    },
    onError: () => {
      NotificationManager.error('Sponsor could not be removed', '', 3000);
    },
  });
}
